import { booleanAttribute } from "#ce/utils/props";

// We had bugs in the past, where the props were mutated. Apply Object.freeze() to prevent this.
export const props = Object.freeze({
  /**
   * Column descriptors, in render order. Crosses the element boundary as a property, never an
   * attribute. `field` keys into the row and `label` heads the column, `align` (`start`, `center` or `end`)
   * and `width` size it, and `formatter(value, row)` returns the cell's content —
   * a string, or a VNode for markup. For sorting: `sortable` renders the affordance,
   * `sortValue(row)` returns the value to sort on when the cell shows a formatted one, and
   * `compare` picks the comparison strategy — either a name (`auto`, `text`, `numeric`, `date`,
   * `boolean`) or a function `(a, b, rowA, rowB)`.
   *
   * @type {Array}
   * @default []
   */
  columns: {
    type: Array,
    default: () => [],
  },
  /**
   * Rows to render. Crosses the element boundary as a property, never an attribute.
   *
   * @type {Array}
   * @default []
   */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Field whose value identifies a row. Rows fall back to their index, which re-uses DOM across
   * a reorder — set this whenever the list can be sorted or filtered.
   *
   * @type {String}
   * @default undefined
   */
  rowKey: {
    type: String,
    default: undefined,
  },
  /**
   * Attributes for each row, as `(row, index) => Object`, merged onto its `<tr>` — a state class, a
   * `data-testid` a journey targets. Crosses the element boundary as a property, never an attribute.
   *
   * @type {Function}
   * @default undefined
   */
  rowAttrs: {
    type: Function,
    default: undefined,
  },
  /**
   * Highlight the row under the pointer. Set it where a row is clickable.
   *
   * @type {Boolean|String}
   * @default false
   */
  hover: booleanAttribute(),
  /**
   * Shade alternate rows.
   *
   * @type {Boolean|String}
   * @default false
   */
  striped: booleanAttribute(),
  /**
   * Hold the header in place while the rows scroll. Needs a height on the element or an ancestor.
   *
   * @type {Boolean|String}
   * @default false
   */
  stickyHeader: booleanAttribute(),
  /**
   * Dim the rows and mark the table busy for assistive tech while new rows are on their way.
   *
   * @type {Boolean|String}
   * @default false
   */
  loading: booleanAttribute(),
  /**
   * Current sort, as `field:direction` (`"requestedAt:desc"`). The table renders the state and
   * emits `sort-change`; it reorders nothing unless `client-sort` is set.
   *
   * @type {String}
   * @default undefined
   */
  sort: {
    type: String,
    default: undefined,
  },
  /**
   * Reorder the rows in place instead of only reporting the intent. For a table holding its whole
   * set — a server-paginated one must leave this off and sort in the query.
   *
   * @type {Boolean|String}
   * @default false
   */
  clientSort: booleanAttribute(),
  /**
   * Sorting strategy: `(items, compare) => items`, receiving a copy it may reorder. Defaults to
   * `Array#sort`. Crosses the element boundary as a property, never an attribute.
   *
   * @type {Function}
   * @default undefined
   */
  sortFn: {
    type: Function,
    default: undefined,
  },
  /**
   * Current page, 1-based. Pagination renders once `per-page` is set.
   *
   * @type {Number|String}
   * @default 1
   * @storybook-control number
   */
  page: {
    type: [Number, String],
    default: 1,
  },
  /**
   * Rows per page. Unset means no pagination.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  perPage: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Total rows across every page. Defaults to what `items` holds, which is only right when the
   * table holds the whole set.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  total: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Which pager to render. `steps` shows the page count with Previous and Next; `pages` shows
   * numbered pages with First and Last, so a reader can jump. The picker beside it appears only
   * when `per-page-options` offers a size other than the current one.
   *
   * @type {String}
   * @default "steps"
   * @values steps, pages
   */
  pagerVariant: {
    type: String,
    default: "steps",
  },
  /**
   * Choices in the rows-per-page picker.
   *
   * @type {Array}
   * @default [10, 25, 50, 100]
   */
  perPageOptions: {
    type: Array,
    default: () => [10, 25, 50, 100],
  },
  /**
   * Slice the rows in place instead of only reporting the intent. For a table holding its whole
   * set — a server-paginated one must leave this off and page in the query.
   *
   * @type {Boolean|String}
   * @default false
   */
  clientPaginate: booleanAttribute(),
  /**
   * Accessible caption for the table. Rendered for assistive tech only.
   *
   * @type {String}
   * @default undefined
   */
  caption: {
    type: String,
    default: undefined,
  },
});
